import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { Temporal } from 'temporal-polyfill';

@Injectable({
	providedIn: 'root'
})
export class DateUtility {
	public diff(startDate: Date, endDate: Date): DateDiffResponse {
		const parsedStartDate = DateTime.fromJSDate(startDate);
		const parsedEndDate = DateTime.fromJSDate(endDate);
		return {
			days: parsedEndDate.diff(parsedStartDate, ['days']).days,
			minutes: parsedEndDate.diff(parsedStartDate, 'minutes').minutes
		};
	}

	public relative(dateTime: string, format: 'short' | 'long' = 'long'): string {
		const pastInstant = Temporal.Instant.from(dateTime);
		const nowInstant = Temporal.Now.instant();
		const duration = nowInstant.since(pastInstant, { largestUnit: 'hour' });

		const totalSeconds = duration.total('second');
		const totalMinutes = duration.total('minute');
		const totalHours = duration.total('hour');
		const totalDays = duration.total('day');

		if (totalSeconds < 10) {
			return "just now";
		}

		// Calculate individual time units
		const days = Math.floor(totalDays);
		const hours = Math.floor(totalHours % 24);
		const minutes = Math.floor(totalMinutes % 60);
		const seconds = Math.floor(totalSeconds % 60);

		if (format === 'short') {
			const parts: string[] = [];
			if (days > 0) parts.push(`${days}d`);
			if (hours > 0) parts.push(`${hours}h`);
			if (minutes > 0) {
				parts.push(`${minutes}m`);
			} else {
				parts.push(`${seconds}s`);
			}
			return parts.join(' ');
		} else {
			// Long format
			const parts: string[] = [];
			if (days >= 1) parts.push(`${days} day${days > 1 ? 's' : ''}`);
			if (hours >= 1) parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
			if (minutes >= 1) {
				parts.push(`${minutes} minute${minutes > 1 ? 's' : ''}`);
			} else {
				parts.push(`${seconds} second${seconds > 1 ? 's' : ''}`);
			}
			return parts.join(' ');
		}
	}


	public relativeWithOffset(date: string): string {
		if (date.length > 24)
			date = date.slice(0, 24);
		const now = new Date();
		const utcNow = new Date(now.getTime() + (now.getTimezoneOffset() * 60000)).getTime();
		const utcDate = new Date(date).getTime();
		return DateTime.fromMillis(utcDate).toRelative({
			base: DateTime.fromMillis(utcNow),
			unit: ['years', 'months', 'days', 'hours', 'minutes']
		});
	}

	public add(date: Date, count: number, unit: DateUnit): Date {
		return DateTime.fromJSDate(date).plus({ [unit]: count }).toJSDate();
	}

	public getDaysInYear(year: number): number {
		const start = new Date(year - 1, 11, 31);
		const end = new Date(year, 11, 31);
		return this.diff(start, end).days;
	}

	public format(date: string, format: string): string {
		const parsedDate = DateTime.fromISO(date);
		return parsedDate.toFormat(format);
	}

	public now(format?: string): string {
		return format ? DateTime.now().toFormat(format) : DateTime.now().toString();
	}

	public static toLocal(date: string): string {
		return DateTime.fromISO(date, {
			zone: 'UTC'
		}).toLocal().toString();
	}

	public static toISO(date: string): string {
		const parsedDate = DateTime.fromISO(date);
		return parsedDate.toISO();
	}

	public static now(format?: string): string {
		return format ? DateTime.now().toFormat(format) : DateTime.now().toString();
	}

	public static formatDateTimeStringToUTC(localDateTimeString: string) : string {
		const localDateTime = new Date(localDateTimeString);
		return new Temporal.PlainDate(localDateTime.getFullYear(), (localDateTime.getMonth() + 1), localDateTime.getDate())
			.toZonedDateTime("UTC").toInstant().toString();
	}
}

interface DateDiffResponse {
	days: number;
	minutes: number;
}

type DateUnit = 'years' | 'quarters' | 'months' | 'weeks' | 'days' | 'hours' | 'minutes' | 'seconds' | 'milliseconds';
